import { useEffect, useRef, useState, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../../../../config/firebaseConfig";

/* ============================================================
   VodGalleryTV — 1920x1080 "Watch Shows" gallery (TV / remote)
   PASS 3:
   - Player buttons no longer resize on focus (outline, not border)
   - Skip indicator: "<< -30" / "+30 >>" flashes center screen
   ============================================================ */

const PLAYER = "https://vod.mywaitserver.live/player5.html";
const HERO_IMG = "/vodhero.jpg";
const THUMB_IMG = "/vodthumb.jpg";
const CHASE_DUR = 1.1;
const GREEN = "#3ADF3C";
const SKIP_SEC = 30;

function fmtDate(iso) {
  const p = iso.split("-");
  const d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
  const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const mon = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return { weekday: days[d.getDay()], monthDay: mon[d.getMonth()] + " " + d.getDate(), year: d.getFullYear() };
}
const chaseDelay = (i) => (-((i % 3) * (CHASE_DUR / 3))).toFixed(3) + "s";

function fmtClock(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s2 = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s2).padStart(2, "0");
  return h > 0 ? h + ":" + mm + ":" + ss : m + ":" + ss;
}

/* ---- resume-from-last-position (localStorage, front-end only) ---- */
const HIST_KEY = (date) => `watchHistory_${date}`;
const NEAR_END_SEC = 300; // watched into last 5 min => treat as finished
const MIN_RESUME_SEC = 30; // ignore tiny progress

function readHistory(date) {
  try {
    const raw = localStorage.getItem(HIST_KEY(date));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.pos !== "number") return null;
    return obj;
  } catch (e) { return null; }
}
function writeHistory(date, pos) {
  try { localStorage.setItem(HIST_KEY(date), JSON.stringify({ pos, at: Date.now() })); } catch (e) {}
}
function clearHistory(date) {
  try { localStorage.removeItem(HIST_KEY(date)); } catch (e) {}
}

const VodGalleryTV = ({ onExit, onVideoActive }) => {
  const [shows, setShows] = useState([]);
  const [playerSrc, setPlayerSrc] = useState(null);
  const [bulbs, setBulbs] = useState([]);
  const [zone, setZone] = useState("hero");
  const [rowIndex, setRowIndex] = useState(0);
  const [playerBtn, setPlayerBtn] = useState("exit");
  const [isPaused, setIsPaused] = useState(false);
  // resume feature
  const [currentShow, setCurrentShow] = useState(null);      // show object playing now
  const [resumePrompt, setResumePrompt] = useState(null);    // { show, pos } while asking
  const [resumeBtn, setResumeBtn] = useState("continue");    // "continue" | "startover"
  const pendingSeekRef = useRef(null);                       // seek once video reports time
  const vodTimeRef = useRef({ cur: 0, dur: 0 });
  // skip indicator: { dir: 1 | -1, id: number } — id restarts the fade animation
  const [skipFlash, setSkipFlash] = useState(null);
  // progress bar: time from player + visibility
  const [vodTime, setVodTime] = useState({ cur: 0, dur: 0 });
  const [showBar, setShowBar] = useState(false);
  const barTimerRef = useRef(null);
  const heroRef = useRef(null);
  const cardRefs = useRef([]);
  const playerFrameRef = useRef(null);
  const skipTimerRef = useRef(null);

  // ---- live listener: shows update instantly when RTDB changes ----
  useEffect(() => {
    const showsRef = ref(database, "vodShows");
    const unsub = onValue(
      showsRef,
      (snap) => {
        const data = snap.val();
        if (!data) { setShows([]); return; }
        const list = Object.keys(data).map((k) => data[k]);
        list.sort((a, b) => (a.date < b.date ? 1 : -1));
        setShows(list);
      },
      () => {} // on error: keep whatever we have
    );
    return () => unsub();
  }, []);

  const computeBulbs = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const ox = hero.offsetLeft, oy = hero.offsetTop;
    const w = hero.offsetWidth, h = hero.offsetHeight;
    const x0 = ox, y0 = oy, x1 = ox + w, y1 = oy + h;
    const sp = 30;
    const pts = [];
    const edge = (ax, ay, bx, by) => {
      const len = Math.hypot(bx - ax, by - ay);
      const n = Math.max(1, Math.round(len / sp));
      for (let i = 0; i < n; i++) {
        const t = i / n;
        pts.push([ax + (bx - ax) * t, ay + (by - ay) * t]);
      }
    };
    edge(x0, y0, x1, y0); edge(x1, y0, x1, y1); edge(x1, y1, x0, y1); edge(x0, y1, x0, y0);
    setBulbs(pts.map((p, i) => ({ x: p[0], y: p[1], delay: chaseDelay(i) })));
  }, []);

  useEffect(() => {
    if (!shows.length) return;
    const t = setTimeout(computeBulbs, 60);
    const onResize = () => computeBulbs();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [shows, computeBulbs]);

  // receive playback time from the player iframe
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev && ev.data;
      if (d && d.vodtime) {
        setVodTime(d.vodtime);
        vodTimeRef.current = d.vodtime;
        // resume: once the video is actually reporting time, jump
        if (pendingSeekRef.current != null && d.vodtime.dur > 0) {
          const t = pendingSeekRef.current;
          pendingSeekRef.current = null;
          try {
            playerFrameRef.current?.contentWindow?.postMessage({ vodctl: "seekto", t }, "*");
          } catch (e) {}
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // save position every 30s while a show is open
  useEffect(() => {
    if (!playerSrc || !currentShow) return;
    const iv = setInterval(() => {
      const t = vodTimeRef.current;
      if (t.dur > 0 && t.cur > MIN_RESUME_SEC) writeHistory(currentShow.date, t.cur);
    }, 30000);
    return () => clearInterval(iv);
  }, [playerSrc, currentShow]);

  const sendToPlayer = useCallback((msg) => {
    try {
      playerFrameRef.current?.contentWindow?.postMessage(msg, "*");
    } catch (e) {}
  }, []);

  const pokeBar = useCallback((paused) => {
    setShowBar(true);
    if (barTimerRef.current) clearTimeout(barTimerRef.current);
    if (!paused) {
      barTimerRef.current = setTimeout(() => setShowBar(false), 2500);
    }
  }, []);

  const flashSkip = useCallback((dir) => {
    setSkipFlash({ dir, id: Date.now() });
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    skipTimerRef.current = setTimeout(() => setSkipFlash(null), 800);
  }, []);

  useEffect(() => () => {
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    if (barTimerRef.current) clearTimeout(barTimerRef.current);
  }, []);

  const closePlayer = useCallback(() => {
    const t = vodTimeRef.current;
    if (currentShow && t.dur > 0) {
      if (t.cur >= t.dur - NEAR_END_SEC) clearHistory(currentShow.date); // finished => fresh next time
      else if (t.cur > MIN_RESUME_SEC) writeHistory(currentShow.date, t.cur);
      else clearHistory(currentShow.date);
    }
    setPlayerSrc(null);
    setCurrentShow(null);
    pendingSeekRef.current = null;
    onVideoActive?.(false);
  }, [currentShow, onVideoActive]);

  const startShow = (show, fromPos) => {
    setIsPaused(false);
    setPlayerBtn("exit");
    setVodTime({ cur: 0, dur: 0 });
    vodTimeRef.current = { cur: 0, dur: 0 };
    setShowBar(false);
    setCurrentShow(show);
    pendingSeekRef.current = fromPos && fromPos > 0 ? fromPos : null;
    onVideoActive?.(true);
    setPlayerSrc(show.master || show.res1080); // TV = ABR off master, 1080-capped in player5
  };

  const openPlayer = (show) => {
    const hist = readHistory(show.date);
    if (hist && hist.pos > MIN_RESUME_SEC) {
      setResumeBtn("continue");
      setResumePrompt({ show, pos: hist.pos });
    } else {
      startShow(show, null);
    }
  };

  // when the show ends, return to the gallery automatically
  useEffect(() => {
    if (!playerSrc) return;
    if (vodTime.dur > 0 && vodTime.cur >= vodTime.dur - 1) {
      closePlayer();
    }
  }, [vodTime, playerSrc, closePlayer]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isUp = e.key === "ArrowUp" || kc === 38;
      const isDown = e.key === "ArrowDown" || kc === 40;
      const isLeft = e.key === "ArrowLeft" || kc === 37;
      const isRight = e.key === "ArrowRight" || kc === 39;
      const isSelect = e.key === "Enter" || e.key === "OK" || e.key === "Select" || kc === 13 || kc === 23 || kc === 66;
      const isBack = e.key === "Escape" || kc === 27 || kc === 461 || kc === 10009;

      // ================= RESUME PROMPT OPEN =================
      if (resumePrompt) {
        if (isUp || isDown || isLeft || isRight) {
          e.preventDefault(); e.stopPropagation();
          setResumeBtn((b) => (b === "continue" ? "startover" : "continue"));
        } else if (isSelect) {
          e.preventDefault(); e.stopPropagation();
          const { show, pos } = resumePrompt;
          setResumePrompt(null);
          if (resumeBtn === "continue") startShow(show, pos);
          else { clearHistory(show.date); startShow(show, null); }
        } else if (isBack) {
          e.preventDefault(); e.stopPropagation();
          setResumePrompt(null);
        }
        return;
      }

      // ================= PLAYER OPEN =================
      if (playerSrc) {
        if (isLeft || isRight) {
          e.preventDefault(); e.stopPropagation();
          sendToPlayer({ vodctl: "seek", delta: isRight ? SKIP_SEC : -SKIP_SEC });
          flashSkip(isRight ? 1 : -1);
          pokeBar(isPaused);
        } else if (isUp || isDown) {
          e.preventDefault(); e.stopPropagation();
          setPlayerBtn((b) => (b === "exit" ? "playpause" : "exit"));
        } else if (isSelect) {
          e.preventDefault(); e.stopPropagation();
          if (playerBtn === "playpause") {
            sendToPlayer({ vodctl: "toggle" });
            setIsPaused((p) => { const np = !p; pokeBar(np); return np; });
          } else {
            closePlayer();
          }
        } else if (isBack) {
          e.preventDefault(); e.stopPropagation();
          closePlayer();
        }
        return;
      }

      // ================= GALLERY =================
      if (isUp || isDown) {
        e.preventDefault(); e.stopPropagation();
        setZone((z) => {
          const hasRow = shows.length > 1;
          if (isDown) {
            if (z === "hero") return hasRow ? "row" : "exit";
            return "exit";
          }
          if (z === "exit") return hasRow ? "row" : "hero";
          return "hero";
        });
      } else if (isLeft || isRight) {
        if (zone === "row") {
          e.preventDefault(); e.stopPropagation();
          setRowIndex((idx) => {
            const next = isRight ? Math.min(idx + 1, shows.length - 2) : Math.max(idx - 1, 0);
            cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
            return next;
          });
        }
      } else if (isSelect) {
        e.preventDefault(); e.stopPropagation();
        if (zone === "hero" && shows[0]) {
          openPlayer(shows[0]);
        } else if (zone === "row" && shows[rowIndex + 1]) {
          openPlayer(shows[rowIndex + 1]);
        } else if (zone === "exit") {
          onExit?.();
        }
      } else if (isBack) {
        e.preventDefault(); e.stopPropagation();
        onExit?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zone, rowIndex, shows, playerSrc, playerBtn, isPaused, resumePrompt, resumeBtn, onExit, sendToPlayer, flashSkip, pokeBar, closePlayer]);

  if (!shows.length) {
    return (
      <div style={S.stage}>
        <div style={S.header}>
          <div style={S.neon}><span style={S.wts}>WAIT TIME SIGNS</span><span style={S.live}>Live!</span></div>
        </div>
        <div style={S.empty}>
          <h2 style={S.emptyH}>The stage is quiet</h2>
          <p>No performances yet.</p>
        </div>
      </div>
    );
  }

  const latest = shows[0];
  const lf = fmtDate(latest.date);
  const heroFocused = zone === "hero";

  return (
    <>
      <div style={S.amber} />
      <div style={S.stage}>
        <div style={S.header}>
          <div style={S.neon} className="vodNeon">
            <span style={S.wts}>WAIT TIME SIGNS</span><span style={S.live}>Live!</span>
          </div>
        </div>

        {/* HERO */}
        <div
          style={{
            ...S.heroWrap,
            transform: heroFocused ? "scale(1.02)" : "scale(1)",
            transition: "transform .18s ease",
          }}
        >
          <section ref={heroRef} style={S.hero}>
            <div style={{ ...S.heroBg, backgroundImage: `url('${HERO_IMG}')` }} />
            <div style={S.heroScrim} />
            <div style={S.heroInner}>
              <span style={S.eyebrow}>Most Recent</span>
              <h1 style={S.heroH1}>
                {lf.weekday} {lf.monthDay}, <span style={{ color: "#f7c04a" }}>{lf.year}</span>
              </h1>
              <button
                onClick={() => openPlayer(latest)}
                style={{
                  ...S.btnWatch,
                  ...(heroFocused
                    ? {
                        outline: `8px solid ${GREEN}`,
                        outlineOffset: "2px",
                        boxShadow: `0 0 14px rgba(58,223,60,.9), 0 0 34px rgba(58,223,60,.55)`,
                        transform: "scale(1.06)",
                      }
                    : {}),
                  transition: "transform .15s ease, box-shadow .15s ease",
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 21, height: 21 }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
                WATCH NOW!
              </button>
            </div>
          </section>
          <div style={S.heroMarquee}>
            {bulbs.map((b, i) => (
              <span key={i} className="vodBulb" style={{ ...S.bulb, left: b.x, top: b.y, animationDelay: b.delay }} />
            ))}
          </div>
        </div>

        {/* PAST PERFORMANCES ROW */}
        {shows.length > 1 && (
        <section style={S.past}>
          <h2 style={S.pastH2}>Past Performances</h2>
          <div style={S.row}>
            {shows.slice(1).map((s, i) => {
              const f = fmtDate(s.date);
              const focused = zone === "row" && rowIndex === i;
              return (
                <div
                  key={s.date}
                  ref={(el) => (cardRefs.current[i] = el)}
                  role="button"
                  onClick={() => openPlayer(s)}
                  style={{
                    ...S.card,
                    ...(focused
                      ? {
                          boxShadow: `0 0 16px rgba(58,223,60,.9), 0 0 40px rgba(58,223,60,.5), 0 14px 34px rgba(0,0,0,.55)`,
                          transform: "translateY(-6px) scale(1.05)",
                          zIndex: 5,
                        }
                      : {}),
                  }}
                >
                  <div style={{ ...S.cardImg, backgroundImage: `url('${THUMB_IMG}')` }} />
                  <div style={S.cardPlate}>{f.monthDay}, {f.year}</div>
                  {focused && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        border: `6px solid ${GREEN}`,
                        borderRadius: 14,
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* EXIT */}
        <button
          onClick={() => onExit?.()}
          style={{
            ...S.exitBtn,
            ...(zone === "exit"
              ? { outline: `6px solid ${GREEN}`, outlineOffset: "2px", boxShadow: `0 0 14px rgba(58,223,60,.8)` }
              : {}),
          }}
        >
          <span style={{ color: "white" }}>EXIT</span>
        </button>
      </div>

      {/* RESUME PROMPT */}
      {resumePrompt && (
        <div style={S.resumeWrap}>
          <div style={S.resumeBox}>
            <div style={S.resumeTitle}>Pick up where you left off?</div>
            <div style={S.resumeSub}>You were at {fmtClock(resumePrompt.pos)}</div>
            <div style={S.resumeBtns}>
              <button
                style={{
                  ...S.resumeBtn,
                  ...(resumeBtn === "continue"
                    ? { outline: `6px solid ${GREEN}`, outlineOffset: "2px", boxShadow: `0 0 14px rgba(58,223,60,.8)` }
                    : {}),
                }}
                onClick={() => { const { show, pos } = resumePrompt; setResumePrompt(null); startShow(show, pos); }}
              >
                CONTINUE
              </button>
              <button
                style={{
                  ...S.resumeBtn,
                  ...(resumeBtn === "startover"
                    ? { outline: `6px solid ${GREEN}`, outlineOffset: "2px", boxShadow: `0 0 14px rgba(58,223,60,.8)` }
                    : {}),
                }}
                onClick={() => { const { show } = resumePrompt; setResumePrompt(null); clearHistory(show.date); startShow(show, null); }}
              >
                START OVER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PLAYER OVERLAY */}
      {playerSrc && (
        <div style={S.overlay}>
          <iframe
            ref={playerFrameRef}
            title="VOD Player"
            src={`${PLAYER}?ui=tv&src=${encodeURIComponent(playerSrc)}`}
            allow="autoplay; fullscreen"
            allowFullScreen
            style={S.overlayFrame}
          />

          {/* skip indicator: chevrons + amount, fades out */}
          {skipFlash && (
            <div key={skipFlash.id} className="vodSkipFlash" style={S.skipFlash}>
              {skipFlash.dir < 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvg} fill="white">
                  <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              )}
              <span style={S.skipText}>{skipFlash.dir > 0 ? "+30" : "-30"}</span>
              {skipFlash.dir > 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvg} fill="white">
                  <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                </svg>
              )}
            </div>
          )}

          {/* progress bar — appears on pause/play/skip, hides while playing */}
          {showBar && vodTime.dur > 0 && (
            <div style={S.progWrap}>
              <span style={S.progTime}>{fmtClock(vodTime.cur)}</span>
              <div style={S.progTrack}>
                <div style={{ ...S.progFill, width: `${Math.min(100, (vodTime.cur / vodTime.dur) * 100)}%` }} />
              </div>
              <span style={S.progTime}>{fmtClock(vodTime.dur)}</span>
            </div>
          )}

          <div style={S.playerBtnStack}>
            <button
              style={{
                ...S.playerBtnBase,
                ...(playerBtn === "playpause"
                  ? { outline: `6px solid ${GREEN}`, outlineOffset: "2px", boxShadow: `0 0 14px rgba(58,223,60,.8)` }
                  : {}),
              }}
              onClick={() => { sendToPlayer({ vodctl: "toggle" }); setIsPaused((p) => { const np = !p; pokeBar(np); return np; }); }}
            >
              {isPaused ? "PLAY" : "PAUSE"}
            </button>
            <button
              style={{
                ...S.playerBtnBase,
                ...(playerBtn === "exit"
                  ? { outline: `6px solid ${GREEN}`, outlineOffset: "2px", boxShadow: `0 0 14px rgba(58,223,60,.8)` }
                  : {}),
              }}
              onClick={closePlayer}
            >
              EXIT
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes vodChase{
          0%,100%{ background:radial-gradient(circle at 38% 32%, #ffe39a, #f7c04a 55%, #9a5a14 100%); box-shadow:0 0 13px 4px rgba(255,227,154,.95); }
          33%,66%{ background:radial-gradient(circle at 38% 32%, #b87e34, #7a4314 60%, #4e2a0e); box-shadow:0 0 3px 1px rgba(247,192,74,.25); }
        }
        @keyframes vodFlicker{
          0%, 86%, 100%{ opacity:1; }
          87%{ opacity:.45; } 88%{ opacity:1; }
          90%{ opacity:.65; } 91%{ opacity:1; }
          93%{ opacity:.35; } 93.6%{ opacity:1; }
          96%{ opacity:.8; } 96.5%{ opacity:1; }
        }
        @keyframes vodSkipFade{
          0%{ opacity:0; transform:translate(-50%,-50%) scale(.85); }
          15%{ opacity:1; transform:translate(-50%,-50%) scale(1); }
          70%{ opacity:1; }
          100%{ opacity:0; transform:translate(-50%,-50%) scale(1.05); }
        }
        .vodBulb{ animation: vodChase 1.1s steps(1,end) infinite; }
        .vodNeon{ animation: vodFlicker 5s infinite steps(60); }
        .vodSkipFlash{ animation: vodSkipFade .8s ease forwards; }
      `}</style>
    </>
  );
};

const S = {
  stage: {
    position: "absolute", top: 0, left: 0, width: "100vw", height: "100vh",
    backgroundColor: "#3f1305",
    backgroundImage: "url('/bricktv.jpg')",
    backgroundSize: "cover", backgroundPosition: "center",
    color: "#fff", fontFamily: "Lato, Arial, sans-serif",
    display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 2500,
  },
  amber: {
    position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2501,
    background:
      "radial-gradient(52% 42% at 50% 6%, rgba(255,176,84,.26) 0%, transparent 62%)," +
      "radial-gradient(42% 52% at 12% 32%, rgba(255,150,60,.12) 0%, transparent 66%)," +
      "radial-gradient(42% 52% at 88% 36%, rgba(255,150,60,.11) 0%, transparent 66%)",
    mixBlendMode: "screen",
  },
  header: { display: "flex", alignItems: "center", padding: "30px 60px 6px" },
  neon: { lineHeight: 1 },
  wts: {
    fontWeight: 900, fontSize: 58, letterSpacing: 3, color: "#eafaff",
    textShadow: "0 0 4px #fff, 0 0 10px #bdecff, 0 0 20px #39b6ff, 0 0 38px #39b6ff, 0 0 60px rgba(57,182,255,.5)",
  },
  live: {
    fontFamily: "Pacifico, cursive", fontSize: 50, color: "#ff5454", marginLeft: 8,
    transform: "rotate(-6deg)", display: "inline-block",
    textShadow: "0 0 6px rgba(255,84,84,.9), 0 0 16px rgba(255,40,40,.6)",
  },
  heroWrap: {
    position: "relative", margin: "26px 60px 0", padding: 20, borderRadius: 20,
    background: "#2a0c04",
    boxShadow: "0 24px 70px rgba(0,0,0,.6), inset 0 0 0 2px rgba(0,0,0,.5), 0 0 60px 8px rgba(255,165,70,.16)",
  },
  hero: {
    position: "relative", zIndex: 1, height: 512, display: "flex", alignItems: "center",
    padding: 50, borderRadius: 10, overflow: "hidden",
  },
  heroBg: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center" },
  heroScrim: {
    position: "absolute", inset: 0,
    background:
      "linear-gradient(180deg, rgba(30,8,2,.3) 0%, rgba(30,8,2,.1) 40%, rgba(30,8,2,.7) 90%, rgba(30,8,2,.85) 100%)," +
      "linear-gradient(90deg, rgba(30,8,2,.9) 0%, rgba(30,8,2,.3) 55%, transparent 100%)",
  },
  heroInner: { position: "relative", maxWidth: 820, transform: "translateY(-26px)" },
  eyebrow: {
    display: "block", marginBottom: 10, fontSize: 26, letterSpacing: 5,
    textTransform: "uppercase", color: "#f7c04a", fontWeight: 900,
  },
  heroH1: {
    fontWeight: 900, fontSize: 68, lineHeight: 1, margin: "0 0 40px", letterSpacing: -1,
    textShadow: "0 2px 22px rgba(0,0,0,.55)",
  },
  btnWatch: {
    display: "inline-flex", alignItems: "center", gap: 14, cursor: "pointer",
    background: "#f7c04a", color: "#3a1500", border: "none", borderRadius: 999,
    padding: "18px 40px", fontFamily: "Lato", fontSize: 22, fontWeight: 900,
  },
  heroMarquee: { position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 },
  bulb: {
    position: "absolute", width: 14, height: 14, margin: "-7px 0 0 -7px", borderRadius: "50%",
    background: "radial-gradient(circle at 38% 32%, #ffe39a 0%, #f7c04a 55%, #9a5a14 100%)",
    boxShadow: "0 0 8px 2px rgba(247,192,74,.55)",
  },
  past: { padding: "40px 0 0 46px", flex: 1, minHeight: 0 },
  pastH2: { fontWeight: 900, fontSize: 34, margin: "0 0 20px" },
  row: {
    display: "flex", gap: 22, overflowX: "auto", overflowY: "hidden",
    padding: "10px 60px 14px 18px", scrollBehavior: "smooth",
  },
  card: {
    position: "relative", flex: "0 0 372px", width: 372,
    borderRadius: 14, overflow: "hidden", cursor: "pointer",
    boxShadow: "0 14px 34px rgba(0,0,0,.55)",
    backgroundColor: "#1a0600",
    transition: "transform .18s ease, box-shadow .18s ease",
  },
  cardImg: {
    width: "100%", aspectRatio: "16/9",
    backgroundSize: "cover", backgroundPosition: "center",
  },
  cardPlate: {
    padding: "10px 16px 12px", textAlign: "center",
    background: "#1c0902", borderTop: "1px solid rgba(255,255,255,.08)",
    color: "#fff", fontSize: 24, fontWeight: 900, fontFamily: "Lato",
    letterSpacing: .5, whiteSpace: "nowrap",
  },
  exitBtn: {
    position: "absolute", right: 60, bottom: 24, zIndex: 2600,
    background: "rgba(0,0,0,0.35)", border: "2px solid white", borderRadius: 8,
    color: "white", fontSize: 30, fontWeight: 700, fontFamily: "Lato",
    height: 70, padding: "0 30px", cursor: "pointer",
  },
  overlay: { position: "absolute", inset: 0, width: "100vw", height: "100vh", backgroundColor: "#000", zIndex: 3000 },
  overlayFrame: { position: "absolute", inset: 0, width: "100vw", height: "100vh", border: "none", pointerEvents: "none" },
  playerBtnStack: {
    position: "absolute", right: 40, bottom: 40, zIndex: 3100,
    display: "flex", flexDirection: "column", gap: 16, alignItems: "stretch",
  },
  playerBtnBase: {
    background: "rgba(0,0,0,0.5)", border: "2px solid white", borderRadius: 10, color: "white",
    fontSize: 30, fontWeight: 700, fontFamily: "Lato",
    height: 70, width: 220, textAlign: "center", cursor: "pointer", outline: "none",
  },
  progWrap: {
    position: "absolute", left: "50%", bottom: 44, transform: "translateX(-50%)",
    zIndex: 3150, display: "flex", alignItems: "center", gap: 22,
    background: "rgba(0,0,0,0.6)", borderRadius: 16, padding: "18px 28px",
    width: "62vw", pointerEvents: "none",
  },
  progTrack: {
    flex: 1, height: 12, borderRadius: 8, background: "rgba(255,255,255,0.25)",
    overflow: "hidden",
  },
  progFill: {
    height: "100%", background: "#f7c04a", borderRadius: 8,
    transition: "width .4s linear",
  },
  progTime: {
    color: "white", fontFamily: "Lato", fontWeight: 700, fontSize: 28,
    fontVariantNumeric: "tabular-nums", minWidth: 110, textAlign: "center",
  },
  skipFlash: {
    position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
    zIndex: 3200, display: "flex", alignItems: "center", gap: 18,
    background: "rgba(0,0,0,0.55)", borderRadius: 20, padding: "22px 38px",
    pointerEvents: "none",
  },
  skipSvg: { width: 64, height: 64 },
  skipText: {
    color: "white", fontFamily: "Lato", fontWeight: 900, fontSize: 56, letterSpacing: 1,
    textShadow: "0 2px 12px rgba(0,0,0,.8)",
  },
  resumeWrap: {
    position: "absolute", inset: 0, zIndex: 2950,
    background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  resumeBox: {
    background: "#1c0902", border: "2px solid rgba(255,255,255,.25)", borderRadius: 20,
    padding: "44px 60px", textAlign: "center",
    boxShadow: "0 30px 80px rgba(0,0,0,.7)",
  },
  resumeTitle: { fontSize: 44, fontWeight: 900, fontFamily: "Lato", color: "#fff", marginBottom: 10 },
  resumeSub: { fontSize: 26, fontFamily: "Lato", color: "#e8c5a8", marginBottom: 34 },
  resumeBtns: { display: "flex", gap: 24, justifyContent: "center" },
  resumeBtn: {
    background: "rgba(0,0,0,0.5)", border: "2px solid white", borderRadius: 10, color: "white",
    fontSize: 28, fontWeight: 700, fontFamily: "Lato",
    height: 70, width: 260, textAlign: "center", cursor: "pointer", outline: "none",
  },
  empty: { textAlign: "center", padding: "100px 20px", color: "#e8c5a8" },
  emptyH: { fontSize: 44, color: "#fff", fontWeight: 900, margin: "0 0 12px" },
};

export default VodGalleryTV;
